import { postHref } from "@/components/post/postHref";
import { langPath, type Lang } from "@/lib/i18n";
import type { Module } from "@/lib/lecturehub/types";

// The module types addressed as `/<segment>/<id>/<slug>`. `post` is NOT missing
// here — it has a real route too, but it is slug-keyed (`/posts/<slug>`), so
// moduleHref() dispatches to postHref() before this lookup. `webinar`, `faq`
// and `collection` have no route yet; they hit the `?? "courses"` last resort
// below, which is a dead link — use canonicalModuleHref() wherever emitting one
// would matter. FAQs and collections never come out of the catalogue queries
// (`modules` leaves them out), but `search` and the assistant's citations do
// return them (09-gotchas, 08-recipes), so those two surfaces go through
// canonicalModuleHref and drop what has no page.
const refTypeToSegment: Record<string, string> = {
  course: "courses",
  event: "events",
  ott: "otts",
  podcast: "podcasts",
};

export function moduleHref(
  lang: Lang,
  m: Pick<Module, "id" | "slug" | "refType">,
  /** Build the in-app (signed-in) link under /library instead of the public
   *  marketing URL. The two surfaces are distinct routes; passing this keeps
   *  in-app navigation client-side (no proxy redirect = preserves SPA feel). */
  library = false,
): string {
  // `post` modules are slug-keyed, not `/[id]/[slug]`. Dispatching here (rather
  // than leaving each caller to special-case it) is what keeps `post` rows out
  // of `/courses/<id>/<slug>` — a URL the wrong-type guard on the detail routes
  // now redirects away from, and which the sitemap used to publish. Live posts
  // exist (api1 ids 166–171), so this is not a theoretical branch.
  if (m.refType === "post") return postHref(lang, m.slug, library);
  const segment = refTypeToSegment[m.refType] ?? "courses";
  const prefix = library ? "/library" : "";
  // Encode the slug segment — backend slugs can contain spaces / diacritics
  // (e.g. "minicity-otroški zaklad-..."), and a raw space in an href is an
  // invalid URL that breaks RSC navigation + prefetch.
  return langPath(lang, `${prefix}/${segment}/${m.id}/${encodeURIComponent(m.slug ?? "")}`);
}

/**
 * `moduleHref` for callers that must not emit a dead link: returns `null`
 * instead of falling back to `/courses` when the module has no URL of its own.
 * Two cases — `webinar` (no route exists), and a `post` with no slug (the slug
 * IS a post's identity, so there is nothing to address).
 *
 * Used by the wrong-type redirect guards on the `[id]/[slug]` detail routes
 * (404 on `null` rather than bouncing to a URL that would 404 anyway) and by
 * `app/sitemap.ts` (skip the row).
 */
export function canonicalModuleHref(
  lang: Lang,
  m: Pick<Module, "id" | "slug" | "refType">,
  library = false,
): string | null {
  if (m.refType === "post") return m.slug ? postHref(lang, m.slug, library) : null;
  if (!(m.refType in refTypeToSegment)) return null;
  return moduleHref(lang, m, library);
}
